/**
 * Sign in. Desktop: Google redirect. Installed iPhone app: Google's in-page button (ID token,
 * no redirect out of the app) plus "Email me a 6-digit code" to the same address.
 */
import { useEffect, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import { env } from '@/lib/env';
import { supabase } from '@/lib/supabase';
import { detectPlatform, isStandalone } from '@/lib/platform';
import { Button } from '@/components/ui/button';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (o: Record<string, unknown>) => void;
          renderButton: (el: HTMLElement, o: Record<string, unknown>) => void;
        };
      };
    };
  }
}

async function sha256Hex(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}

function loadGis(): Promise<void> {
  if (window.google?.accounts) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Could not load Google sign-in'));
    document.head.appendChild(s);
  });
}

export function SignIn() {
  const iosApp = detectPlatform() === 'ios' && isStandalone();
  const [error, setError] = useState<string | null>(null);
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const gisRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!iosApp || !env.googleClientId || !gisRef.current) return;
    let cancelled = false;
    void (async () => {
      try {
        await loadGis();
        const raw = crypto.randomUUID();
        const hashed = await sha256Hex(raw);
        if (cancelled || !window.google || !gisRef.current) return;
        window.google.accounts.id.initialize({
          client_id: env.googleClientId,
          nonce: hashed,
          ux_mode: 'popup',
          use_fedcm_for_button: true,
          callback: async (resp: { credential: string }) => {
            const { error: err } = await supabase!.auth.signInWithIdToken({
              provider: 'google',
              token: resp.credential,
              nonce: raw,
            });
            if (err) setError(err.message);
          },
        });
        window.google.accounts.id.renderButton(gisRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          width: 300,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [iosApp]);

  const google = async () => {
    setBusy(true);
    const { error: err } = await supabase!.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin, queryParams: { prompt: 'select_account' } },
    });
    if (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase!.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: true },
    });
    setBusy(false);
    if (err) setError(err.message);
    else setSent(true);
  };
  const verify = async () => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase!.auth.verifyOtp({
      email: email.trim(),
      token: code.trim(),
      type: 'email',
    });
    setBusy(false);
    if (err)
      setError(
        err.message.includes('expired')
          ? 'That code has expired or is wrong. Ask for a new one.'
          : err.message,
      );
  };

  return (
    <main className="flex min-h-full items-center justify-center bg-surface-1 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-5 rounded-lg border border-line bg-raised p-8 shadow-card">
        <img src="/icons/icon.svg" alt="" width={64} height={64} />
        <div className="text-center">
          <h1 className="text-2xl font-semibold">Karya</h1>
          <p className="text-ink-2">Your work, in order.</p>
        </div>
        {!emailMode ? (
          <>
            {iosApp ? (
              <div ref={gisRef} className="flex min-h-11 justify-center" />
            ) : (
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => void google()}
                disabled={busy}
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path
                    fill="#FFC107"
                    d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
                  />
                  <path
                    fill="#FF3D00"
                    d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
                  />
                  <path
                    fill="#4CAF50"
                    d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
                  />
                  <path
                    fill="#1976D2"
                    d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
                  />
                </svg>
                Continue with Google
              </Button>
            )}
            <button
              className="flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink"
              onClick={() => setEmailMode(true)}
            >
              <Mail className="size-4" />
              Email me a 6-digit code instead
            </button>
          </>
        ) : (
          <form
            className="flex w-full flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void (sent ? verify() : sendCode());
            }}
          >
            <label className="flex flex-col gap-1 text-sm text-ink-2">
              Your email
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={sent}
                className="h-11 rounded-sm border border-line bg-bg px-3 text-base text-ink outline-none focus:border-primary"
              />
            </label>
            {sent && (
              <label className="flex flex-col gap-1 text-sm text-ink-2">
                6-digit code from the email
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="h-11 rounded-sm border border-line bg-bg px-3 text-center text-xl tracking-[0.4em] text-ink outline-none focus:border-primary"
                />
              </label>
            )}
            <Button type="submit" variant="primary" size="lg" disabled={busy}>
              {sent ? 'Sign in' : 'Email me a code'}
            </Button>
            <button
              type="button"
              className="text-sm text-ink-3 hover:text-ink"
              onClick={() => (sent ? setSent(false) : setEmailMode(false))}
            >
              {sent ? 'Use a different email' : 'Back to Google sign-in'}
            </button>
          </form>
        )}
        {error && (
          <p role="alert" className="text-center text-sm text-overdue">
            {error}
          </p>
        )}
        <p className="text-center text-cap text-ink-3">
          Karya is invite-only. Use the Google account or email address that was invited.
        </p>
        <a href="/?demo=1" className="text-cap text-ink-3 underline-offset-2 hover:underline">
          Look around with demo data
        </a>
      </div>
    </main>
  );
}

export function NotAllowed({ email }: { email: string | null }) {
  return (
    <main className="flex min-h-full items-center justify-center bg-surface-1 p-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-lg border border-line bg-raised p-8 text-center shadow-card">
        <img src="/icons/icon.svg" alt="" width={56} height={56} />
        <h1 className="text-xl font-semibold">Karya is invite-only</h1>
        <p className="text-ink-2">
          {email ?? 'This account'} isn’t on the invite list yet. Ask the person who runs this Karya
          to add your email in Settings → Access, then sign in again.
        </p>
        <Button onClick={() => void supabase?.auth.signOut()}>Sign out</Button>
      </div>
    </main>
  );
}
